import { Linking, Platform } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useInitiateIndividualConversation } from '@hooks'
import { useMessageDraftStore } from '@store'
import { useLanguage } from '@context'
import { showApiErrorToast } from '@utils'

/** A phone number as dialled (digits and a leading +), or null when it has too few digits. */
export const dialable = (phone?: string | null): string | null => {
  const cleaned = (phone ?? '').trim().replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '')
  return cleaned.replace(/\D/g, '').length >= 4 ? cleaned : null
}

/** A route to a place: Apple Maps on iPhone, Google Maps elsewhere. */
export const routeUrl = (place: string, os: string = Platform.OS): string => {
  const destination = encodeURIComponent(place.trim())
  return os === 'ios'
    ? `http://maps.apple.com/?daddr=${destination}`
    : `https://www.google.com/maps/dir/?api=1&destination=${destination}`
}

/**
 * Ways to reach someone from a notification: the conversation (with a
 * message ready to send, when given), a call, a text, an e-mail, or the
 * route to a rendez-vous.
 */
export const useReachOut = () => {
  const navigation = useNavigation()
  const { t } = useLanguage()
  const { mutateAsync: initiateConversation } = useInitiateIndividualConversation()
  const startDraft = useMessageDraftStore((s) => s.startDraft)
  const fillDraft = useMessageDraftStore((s) => s.fillDraft)

  const openUrl = (url: string) => {
    Linking.openURL(url).catch(() => undefined)
  }

  const openConversation = async (personId: string, draft?: string) => {
    try {
      const { id } = await initiateConversation({ participantAccountId: personId })
      if (draft) {
        startDraft(id)
        fillDraft(id, draft)
      }
      ;(navigation as any).navigate('Messages', { screen: 'Conversation', params: { conversationId: id } })
    } catch (error) {
      showApiErrorToast(t, error)
    }
  }

  return {
    openConversation,
    call: (phone: string) => openUrl(`tel:${phone}`),
    sms: (phone: string) => openUrl(`sms:${phone}`),
    email: (address: string) => openUrl(`mailto:${address.trim()}`),
    route: (place: string) => openUrl(routeUrl(place)),
  }
}
